/*!
 * SPDX-License-Identifier: AGPL-3.0-only
 * 
 * PV Tool — AGPL Community Edition
 * Based on the last AGPL-3.0 version published on 2026-03-18
 * 
 * Copyright (c) 2026 DanteAlighieri13210914
 * Copyright (c) 2026 Contributors to PV Tool AGPL Community Edition
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, version 3 of the License.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 * 
 * Source repository: https://github.com/yandujun363/pv-tool-agpl
 */

import * as PIXI from 'pixi.js';
import { BaseEffect } from './base';
import type { UpdateContext } from '../core/types';
import { resolveColor } from '../core/types';

/**
 * L-shaped ruler/scale with tick marks forming a right angle,
 * and a circle marker at the intersection point.
 */
export class RulerGuide extends BaseEffect {
  readonly name = 'rulerGuide';
  private g!: PIXI.Graphics;
  private drawn = false;
  private lastW = 0;
  private lastH = 0;

  protected setup(): void {
    this.g = new PIXI.Graphics();
    this.container.addChild(this.g);
  }

  update(ctx: UpdateContext): void {
    if (this.drawn && this.lastW === ctx.screenWidth && this.lastH === ctx.screenHeight) return;
    this.drawn = true;
    this.lastW = ctx.screenWidth;
    this.lastH = ctx.screenHeight;

    const g = this.g;
    g.clear();

    const w = ctx.screenWidth;
    const h = ctx.screenHeight;
    const color = resolveColor(this.config.color ?? '#ffffff', this.palette);
    const alpha = this.config.alpha ?? 0.5;
    const originX = w * (this.config.x ?? 0.12);
    const originY = h * (this.config.y ?? 0.75);
    const hLen = w * (this.config.hLength ?? 0.85);
    const vLen = h * (this.config.vLength ?? 0.65);
    const lineWidth = this.config.lineWidth ?? 1;

    // Main axes
    // Horizontal: from origin to the right
    g.moveTo(originX, originY).lineTo(originX + hLen, originY);
    // Vertical: from origin upward
    g.moveTo(originX, originY).lineTo(originX, originY - vLen);
    g.stroke({ color, width: lineWidth, alpha });

    // Tick marks on horizontal axis
    const tickSpacing = this.config.tickSpacing ?? 12;
    const majorEvery = this.config.majorEvery ?? 5;
    const minorLen = this.config.minorTickLen ?? 6;
    const majorLen = this.config.majorTickLen ?? 14;

    let tickIdx = 0;
    for (let x = originX; x <= originX + hLen; x += tickSpacing) {
      const isMajor = tickIdx % majorEvery === 0;
      const tl = isMajor ? majorLen : minorLen;
      g.moveTo(x, originY).lineTo(x, originY - tl);
      tickIdx++;
    }
    g.stroke({ color, width: 0.8, alpha: alpha * 0.7 });

    // Tick marks on vertical axis
    tickIdx = 0;
    for (let y = originY; y >= originY - vLen; y -= tickSpacing) {
      const isMajor = tickIdx % majorEvery === 0;
      const tl = isMajor ? majorLen : minorLen;
      g.moveTo(originX, y).lineTo(originX + tl, y);
      tickIdx++;
    }
    g.stroke({ color, width: 0.8, alpha: alpha * 0.7 });

    // Circle marker at origin
    const circleR = this.config.circleRadius ?? 8;
    g.circle(originX, originY, circleR);
    g.stroke({ color, width: 1.2, alpha: alpha * 0.9 });
    // Crosshair inside circle
    g.moveTo(originX - circleR * 0.6, originY).lineTo(originX + circleR * 0.6, originY);
    g.moveTo(originX, originY - circleR * 0.6).lineTo(originX, originY + circleR * 0.6);
    g.stroke({ color, width: 0.8, alpha: alpha * 0.6 });
    // Center dot
    g.circle(originX, originY, 1.5);
    g.fill({ color, alpha: alpha * 0.8 });
  }
}